/**
 * threadOrganize — the organization surface the host hands every thread list
 * (2026-09-09, Miles: "the whole experience of categorizing threads and a user
 * making their own organization of thread should be an extremely great user
 * experience"). One API object; the rail and the sheet wire the same
 * gestures to it: section rename / move / collapse / delete, "move to…" from
 * any row, drag a row onto a section, undo in place.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { ChatThread } from './types';
import type { ThreadCategory, ThreadListLang } from '../../types/threadCategories';
import type { CategoryMoveDirection, CategoryMoveOptions, RenameCategoryOptions, RenameCategoryResult } from '../../hooks/useThreadCategories';

export interface ThreadListNoticeState {
  id: number;
  text: string;
  /** Present when the change can be undone in place. */
  undo?: () => void;
}

export interface ThreadOrganizeApi {
  lang: ThreadListLang;
  /**
   * Whether this viewer may ARRANGE their own list — rename a thread for
   * themselves, and create / rename / reorder / delete / collapse their
   * sections (catalog v13 `threads.organize-own`, 2026-09-13). ABSENT reads as
   * true, so every caller that predates the key behaves exactly as it did.
   *
   * The list itself is unaffected: sectioning, fold aliases and natural
   * sections all still come from this object, so a viewer without the key sees
   * the same rail — it simply offers them no arranging gestures (PR-1's rule:
   * never a button that fails after the click).
   */
  canOrganize?: boolean;
  /** Every section in the viewer's order (custom + built-ins). */
  categories: readonly ThreadCategory[];
  collapsed: ReadonlySet<string>;
  foldAliases: Readonly<Record<string, string>>;
  /** The built-in id an UNFILED thread falls into (its natural section). */
  naturalSectionIdFor: (thread: Pick<ChatThread, 'id' | 'sharedThread'>) => string;
  /** File a thread under a section (custom or built-in id), or under none (its natural section). */
  moveThread: (threadId: string, categoryId: string | null) => void;
  renameThread: (threadId: string, title: string) => void;
  createCategory: (name: string) => Promise<string | null>;
  /**
   * Rename a section. A name another section already wears comes back as
   * `{reason:'clash', clash}` rather than a flat refusal (2026-09-16), so the
   * surface can ask "move everything there?" and call again with `mergeInto`.
   */
  renameCategory: (id: string, name: string, opts?: RenameCategoryOptions) => Promise<RenameCategoryResult>;
  removeCategory: (id: string) => Promise<boolean>;
  /** Move a section; `among` (the visible section ids, in order) makes the move relative to the visible neighbour. */
  moveCategory: (id: string, direction: CategoryMoveDirection, opts?: CategoryMoveOptions) => Promise<boolean>;
  setCollapsed: (id: string, collapsed: boolean) => void;
  notice: ThreadListNoticeState | null;
  dismissNotice: () => void;
}

/**
 * The ONE predicate every list surface asks before offering an arranging
 * gesture. Kept here rather than repeated as `!!organize` in five components,
 * which is how the two halves would drift apart.
 */
export function canOrganizeThreads(organize: ThreadOrganizeApi | null | undefined): boolean {
  return !!organize && organize.canOrganize !== false;
}

export const RAIL_MENU_WIDTH = 208;

/**
 * An anchored body-portal menu's state (the ThreadHeaderOverflowMenu
 * contract): fixed coordinates measured from the trigger at open time,
 * clamped to the viewport; focus the first item on open; Escape closes and
 * refocuses the trigger; an outside mousedown closes.
 */
export function useAnchoredMenu(buttonRef: RefObject<HTMLElement>, opts?: { width?: number; above?: boolean }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 8, top: 56 });
  const menuRef = useRef<HTMLDivElement>(null);
  const width = opts?.width ?? RAIL_MENU_WIDTH;

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }, [buttonRef]);

  const openAt = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      const viewportWidth = window.innerWidth || width;
      setPos({
        left: Math.max(8, Math.min(rect.left, viewportWidth - width - 8)),
        top: Math.max(8, rect.bottom + 6),
      });
    }
    setOpen(true);
  }, [buttonRef, width]);

  const toggle = useCallback(() => {
    if (open) close(false);
    else openAt();
  }, [open, close, openAt]);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"], [role="menuitemradio"], input')?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [open, close, buttonRef]);

  return { open, pos, menuRef, toggle, close, openAt };
}

/** A transient in-list notice with an optional undo (the host owns the timer). */
export function useThreadListNotice(ttlMs: number = 6000) {
  const [notice, setNotice] = useState<ThreadListNoticeState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);
  const dismissNotice = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setNotice(null);
  }, []);
  const showNotice = useCallback((text: string, undo?: () => void) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    seqRef.current += 1;
    const id = seqRef.current;
    setNotice({ id, text, ...(undo ? { undo } : {}) });
    timerRef.current = setTimeout(() => {
      setNotice((current) => (current?.id === id ? null : current));
      timerRef.current = null;
    }, ttlMs);
  }, [ttlMs]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return { notice, showNotice, dismissNotice };
}

/** True on a device whose primary pointer can hover (drag-and-drop is offered there). */
export function finePointerAvailable(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return false;
  }
}
